import { ApolloError } from "@apollo/client";
import React, {
    ReactElement, useContext, useEffect, useMemo,
} from "react";
import { Loading } from "../components";
import { ErrorDefault } from "../pages/error/Error";
import {
    Permission, PortalBannerType, useGetCurrentPortalLazyQuery, useGetCurrentPortalQuery,
} from "../__generated__/api";
import useLogging from "./logging/LoggingContext";

import { upgradeClientDomain } from "../api";

const REFRESH_INTERVAL = 1000 * 60 * 60;

const KEY = "portal";

const FEATURE_NEW_DOMAIN = "frontend.update.api.domain";

export interface PortalBanner {
    expirationDate: Date;
    message: string;
    linkUrl: string;
    type: PortalBannerType
}

export interface Portal {
    id?: string;
    title?: string;
    welcomeMessage?: string;
    chatToken?: string;
    lastUpdated: Date;
    indexArticleId?: string;
    permissions: Set<Permission>;
    invalidate: () => void;
    clearCache: () => void;
    featureFlags: Set<string>;
    isAIEnabled: boolean;
    banner?: Partial<PortalBanner>;
    loginUrl?: string;
}

interface PortalSerialized {
    title?: string;
    id?: string;
    welcomeMessage?: string;
    chatToken?: string;
    indexArticleId?: string;
    lastUpdated: number;
    permissions: Permission[];
    featureFlags: string[];
    isAIEnabled: boolean;
    trialExpiration: string;
    expirationReminderDate: string;
    banner?: Partial<PortalBanner>
    loginUrl?: string;
}

export const PortalContext = React.createContext<Portal>({
    lastUpdated: new Date(),
    permissions: new Set(),
    invalidate: () => { /* do nothing */ },
    clearCache: () => { /* do nothing */ },
    featureFlags: new Set(),
    isAIEnabled: false,
});

export function PortalProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const {
        loading, data, error, refetch,
    } = useGetCurrentPortalQuery();

    const logging = useLogging();

    const clearCache = () => {
        localStorage.removeItem(KEY);
    };

    const invalidate = () => {
        clearCache();
        refetch();
    };

    const portal: Portal | undefined = useMemo(() => {
        let current: Portal | undefined;
        if (data?.portal) {
            current = {
                id: data.portal.id,
                chatToken: data.portal.chatToken as string,
                title: data.portal.title as string,
                welcomeMessage: data.portal.welcomeMessage as string,
                permissions: new Set(data.portal.permissions),
                lastUpdated: new Date(),
                indexArticleId: data.portal.indexArticleId,
                invalidate,
                clearCache,
                featureFlags: new Set(data.featureFlags?.active ?? []),
                isAIEnabled: data.portal.isAIEnabled as boolean,
                banner: {
                    ...data.portal.banner,
                    expirationDate: data.portal.banner?.expirationDate
                        ? new Date(data.portal.banner?.expirationDate)
                        : undefined,
                } as PortalBanner,
                loginUrl: data.portal.singleSignOn?.isEnabled ? data.portal.singleSignOn?.loginRedirectUrl as string : undefined,
            };
        }

        if (current) {
            localStorage.setItem(KEY, JSON.stringify({
                ...current,
                lastUpdated: current.lastUpdated.getTime(),
                permissions: Array.from(current.permissions),
                featureFlags: Array.from(current.featureFlags),
            }));
        } else {
            const cachedPortal = localStorage.getItem(KEY);
            if (cachedPortal) {
                const localPortal = JSON.parse(cachedPortal) as PortalSerialized;
                current = {
                    id: localPortal.id,
                    lastUpdated: new Date(localPortal.lastUpdated),
                    permissions: new Set(localPortal.permissions),
                    chatToken: localPortal.chatToken,
                    title: localPortal.title,
                    welcomeMessage: localPortal.welcomeMessage,
                    indexArticleId: localPortal.indexArticleId,
                    invalidate,
                    clearCache,
                    isAIEnabled: localPortal.isAIEnabled,
                    featureFlags: new Set(data?.featureFlags?.active ?? localPortal.featureFlags),
                    banner: {
                        ...localPortal.banner,
                        expirationDate: localPortal.banner?.expirationDate
                            ? new Date(localPortal.banner?.expirationDate)
                            : undefined,
                    },
                    loginUrl: localPortal.loginUrl,
                };
            }
        }

        if (current?.featureFlags.has(FEATURE_NEW_DOMAIN)) {
            upgradeClientDomain();
        }

        return current;
    }, [data?.portal]);

    useEffect(() => {
        if (data?.portal) {
            logging.info({
                portalId: data.portal.id,
                permissions: Array.from(data.portal.permissions),
                featureFlags: Array.from(data.featureFlags?.active ?? []),
            }, "Portal Started");
        }
    }, [data?.portal?.id]);

    if (error) {
        return <ErrorDefault message={error instanceof ApolloError ? error.message : "Unknown error occured."} />;
    }
    if (loading) {
        return <Loading />;
    }

    if (!portal) {
        return <Loading />;
    }

    return (
        <PortalContext.Provider value={portal}>
            {children}
        </PortalContext.Provider>
    );
}

export default function usePortal(): Portal {
    return useContext(PortalContext) ?? {};
}
